import type { WebRow } from '../api/market';

interface CountRow { label: string; value: number }
type AiReferrals = { kind: 'unavailable' } | {
  kind: 'available'; pageviews: number; providers: CountRow[];
};
type BusinessActions = { kind: 'unavailable' } | {
  kind: 'available'; actions: CountRow[];
};

const PROVIDERS: [string, string[]][] = [
  ['ChatGPT', ['chatgpt.com', 'chat.openai.com']],
  ['Claude', ['claude.ai']],
  ['Perplexity', ['perplexity.ai']],
  ['Gemini', ['gemini.google.com']],
  ['Copilot', ['copilot.microsoft.com']],
  ['Grok', ['grok.com']],
  ['DeepSeek', ['chat.deepseek.com']],
];

export function classifyAiReferrer(source: string): string | null {
  try {
    const url = new URL(source.includes('://') ? source : `https://${source}`);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    const host = url.hostname.toLowerCase().replace(/\.$/, '');
    return PROVIDERS.find(([, domains]) => domains.some(
      (domain) => host === domain || host.endsWith(`.${domain}`),
    ))?.[0] ?? null;
  } catch {
    return null;
  }
}

function count(value: WebRow[string]): number {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function sorted(totals: Map<string, number>): CountRow[] {
  return Array.from(totals, ([label, value]) => ({ label, value }))
    .filter((row) => row.value > 0)
    .sort((a, b) => b.value - a.value || a.label.localeCompare(b.label));
}

export function summarizeAiReferrals(rows: WebRow[] | null): AiReferrals {
  if (!Array.isArray(rows)) return { kind: 'unavailable' };
  const totals = new Map<string, number>();
  for (const row of rows) {
    const provider = classifyAiReferrer(String(row.source ?? ''));
    if (provider) totals.set(provider, (totals.get(provider) ?? 0) + count(row.total_views));
  }
  const providers = sorted(totals);
  return { kind: 'available', pageviews: providers.reduce((sum, row) => sum + row.value, 0), providers };
}

export function summarizeBusinessActions(rows: WebRow[] | null): BusinessActions {
  if (!Array.isArray(rows)) return { kind: 'unavailable' };
  const totals = new Map<string, number>();
  for (const row of rows) {
    const target = String(row.cta_target ?? '');
    let label: string | null = null;
    if (target === 'teams_guide_cta' || target === 'teams_buyer_guide_cta') label = 'Teams page from guides';
    else if (target.startsWith('teams_app_store_')) label = 'App Store from Teams';
    else if (target === 'sample_report_view') label = 'Sample report opened';
    else if (target === 'sample_report_download') label = 'Sample CSV download clicks';
    else if (target === 'teams_multi_location') label = 'Multi-location inquiry opened';
    if (label) totals.set(label, (totals.get(label) ?? 0) + count(row.total_clicks));
  }
  return { kind: 'available', actions: sorted(totals) };
}
